import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Button } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import type { InvoiceSurcharge, SurchargeTipo } from '@/shared/billing/invoiceSurcharges';
import type { ClinicInvoiceDTO } from './ClinicInvoiceModal';
import { formatMoney } from './ClinicInvoiceDocument';

type Pending =
  | { action: 'remove'; tipo: SurchargeTipo }
  | { action: 'exempt' }
  | null;

function apiMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: unknown } } })?.response?.data;
  return typeof data?.message === 'string' && data.message.trim() ? data.message : i18next.t('common.error');
}

const TEXTAREA_CLASS =
  'min-h-[64px] w-full resize-y rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500';

/**
 * Recargos de mora / reconexión de la factura y exención de la clínica.
 * Aplicar o quitar a mano (quitar pide motivo); todo queda en Auditorías.
 */
export function InvoiceSurchargePanel({
  invoice,
  tenantId,
  period,
  disabled,
  onUpdated,
}: {
  invoice: ClinicInvoiceDTO;
  tenantId: string;
  period: string;
  disabled: boolean;
  onUpdated: (next: ClinicInvoiceDTO) => void;
}) {
  const { t, i18n } = useTranslation();
  const { success, error: showError } = useToast();
  const [pending, setPending] = useState<Pending>(null);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);

  const money = (n: number) => formatMoney(n, i18n.language, invoice.moneda);
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language) : '—');
  const surcharges = invoice.surcharges ?? [];
  const byTipo = (tipo: SurchargeTipo): InvoiceSurcharge | null => surcharges.find((s) => s.tipo === tipo) ?? null;
  const exemption = invoice.surchargeExemption ?? null;
  const editable = invoice.status === 'confirmed' && !invoice.paidAt;
  const base = encodeURIComponent(tenantId);

  const run = async (request: () => Promise<ClinicInvoiceDTO | null>, okMessage: string) => {
    setBusy(true);
    try {
      const next = await request();
      if (next) onUpdated(next);
      success(okMessage);
      setPending(null);
      setMotivo('');
    } catch (err) {
      showError(apiMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const reload = async () =>
    (await httpClient.get<ClinicInvoiceDTO>(`/panel-pagos/factura/${base}`, { params: { periodo: period } })).data;

  const apply = (tipo: SurchargeTipo, porcentaje?: number) =>
    run(
      async () =>
        (await httpClient.post<ClinicInvoiceDTO>(`/panel-pagos/factura/${base}/recargos`, { periodo: period, tipo, porcentaje }))
          .data,
      i18next.t('pages.clinicInvoices.surcharges.applied')
    );

  const confirmPending = () => {
    if (!pending) return;
    if (!motivo.trim()) {
      showError(i18next.t('pages.clinicInvoices.surcharges.reasonRequired'));
      return;
    }
    if (pending.action === 'remove') {
      void run(
        async () =>
          (
            await httpClient.post<ClinicInvoiceDTO>(`/panel-pagos/factura/${base}/recargos/${pending.tipo}/quitar`, {
              periodo: period,
              motivo: motivo.trim(),
            })
          ).data,
        i18next.t('pages.clinicInvoices.surcharges.removed')
      );
    } else {
      void run(async () => {
        await httpClient.put(`/panel-pagos/clinica/${base}/exencion-recargos`, { motivo: motivo.trim() });
        return reload();
      }, i18next.t('pages.clinicInvoices.surcharges.exemptSaved'));
    }
  };

  const clearExemption = () =>
    run(async () => {
      await httpClient.delete(`/panel-pagos/clinica/${base}/exencion-recargos`);
      return reload();
    }, i18next.t('pages.clinicInvoices.surcharges.exemptCleared'));

  const isBusy = busy || disabled;
  const mora = byTipo('mora');
  const rec = byTipo('reconexion');
  const moraActive = mora && !mora.quitadoAt ? mora : null;
  const recActive = rec && !rec.quitadoAt ? rec : null;

  const row = (tipo: SurchargeTipo, s: InvoiceSurcharge | null) => {
    const label =
      tipo === 'mora'
        ? t('pages.clinicInvoices.surcharges.mora')
        : t('pages.clinicInvoices.surcharges.reconexion');
    return (
      <div key={tipo} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900">
              {label}
              {s && !s.quitadoAt ? ` · ${s.porcentaje}% · ${money(s.monto)}` : ''}
            </p>
            {!s ? (
              <p className="mt-0.5 text-xs text-slate-500">{t('pages.clinicInvoices.surcharges.notApplied')}</p>
            ) : s.quitadoAt ? (
              <p className="mt-0.5 text-xs text-slate-500">
                {t('pages.clinicInvoices.surcharges.removedBy', {
                  by: s.quitadoPor ?? '—',
                  at: when(s.quitadoAt),
                  reason: s.motivoQuitado ?? '—',
                })}
              </p>
            ) : (
              <p className="mt-0.5 text-xs text-slate-500">
                {t(s.origen === 'auto' ? 'pages.clinicInvoices.surcharges.appliedAuto' : 'pages.clinicInvoices.surcharges.appliedManual', {
                  by: s.aplicadoPor ?? '—',
                  at: when(s.aplicadoAt),
                })}
              </p>
            )}
          </div>
          {editable ? (
            <div className="flex flex-wrap gap-2">
              {/* Exenta: no se aplican recargos nuevos, pero se pueden quitar los que ya tenía. */}
              {!exemption && tipo === 'mora' && (!moraActive || moraActive.porcentaje !== 15) ? (
                <Button size="sm" variant="secondary" disabled={isBusy} onClick={() => void apply('mora', 15)}>
                  {t('pages.clinicInvoices.surcharges.applyMora', { pct: 15 })}
                </Button>
              ) : null}
              {!exemption && tipo === 'mora' && (!moraActive || moraActive.porcentaje !== 30) ? (
                <Button size="sm" variant="secondary" disabled={isBusy} onClick={() => void apply('mora', 30)}>
                  {t('pages.clinicInvoices.surcharges.applyMora', { pct: 30 })}
                </Button>
              ) : null}
              {!exemption && tipo === 'reconexion' && !recActive ? (
                <Button size="sm" variant="secondary" disabled={isBusy} onClick={() => void apply('reconexion')}>
                  {t('pages.clinicInvoices.surcharges.applyReconexion')}
                </Button>
              ) : null}
              {s && !s.quitadoAt ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={isBusy}
                  onClick={() => {
                    setPending({ action: 'remove', tipo });
                    setMotivo('');
                  }}
                >
                  {t('pages.clinicInvoices.surcharges.remove')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        {pending?.action === 'remove' && pending.tipo === tipo ? (
          <ReasonForm
            value={motivo}
            onChange={setMotivo}
            busy={busy}
            placeholder={t('pages.clinicInvoices.surcharges.removeReasonPlaceholder')}
            confirmLabel={t('pages.clinicInvoices.surcharges.removeConfirm')}
            cancelLabel={t('common.cancel')}
            onCancel={() => setPending(null)}
            onConfirm={confirmPending}
          />
        ) : null}
      </div>
    );
  };

  // Sin recargos ni exención en una factura que no se puede tocar: no hay nada que mostrar.
  if (!editable && surcharges.length === 0 && !exemption) return null;

  return (
    <div className="mx-auto mt-4 max-w-[210mm] rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <h3 className="text-sm font-semibold text-slate-900">{t('pages.clinicInvoices.surcharges.title')}</h3>
      <p className="mt-0.5 text-xs text-slate-500">{t('pages.clinicInvoices.surcharges.hint')}</p>

      <div className="mt-3 rounded-lg border border-slate-200 p-3">
        {exemption ? (
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-800">{t('pages.clinicInvoices.surcharges.exemptOn')}</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {t('pages.clinicInvoices.surcharges.exemptBy', {
                  by: exemption.createdBy ?? '—',
                  at: when(exemption.createdAt),
                  reason: exemption.motivo,
                })}
              </p>
            </div>
            <Button size="sm" variant="secondary" disabled={isBusy} onClick={() => void clearExemption()}>
              {t('pages.clinicInvoices.surcharges.exemptClear')}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="text-xs text-slate-500">{t('pages.clinicInvoices.surcharges.exemptOff')}</p>
              <Button
                size="sm"
                variant="secondary"
                disabled={isBusy}
                onClick={() => {
                  setPending({ action: 'exempt' });
                  setMotivo('');
                }}
              >
                {t('pages.clinicInvoices.surcharges.exemptSet')}
              </Button>
            </div>
            {pending?.action === 'exempt' ? (
              <ReasonForm
                value={motivo}
                onChange={setMotivo}
                busy={busy}
                placeholder={t('pages.clinicInvoices.surcharges.exemptReasonPlaceholder')}
                confirmLabel={t('pages.clinicInvoices.surcharges.exemptSet')}
                cancelLabel={t('common.cancel')}
                onCancel={() => setPending(null)}
                onConfirm={confirmPending}
              />
            ) : null}
          </>
        )}
      </div>

      {editable || surcharges.length > 0 ? (
        <div className="mt-3 space-y-2">
          {row('mora', mora)}
          {row('reconexion', rec)}
        </div>
      ) : null}
    </div>
  );
}

function ReasonForm({
  value,
  onChange,
  busy,
  placeholder,
  confirmLabel,
  cancelLabel,
  onCancel,
  onConfirm,
}: {
  value: string;
  onChange: (v: string) => void;
  busy: boolean;
  placeholder: string;
  confirmLabel: string;
  cancelLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="mt-3 space-y-2">
      <textarea
        className={TEXTAREA_CLASS}
        value={value}
        maxLength={1000}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button size="sm" onClick={onConfirm} disabled={busy || !value.trim()} isLoading={busy}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
